/**
 * The expense rules, shared in spirit with the Reimburse phone app.
 *
 * Both apps write the same PocketBase records, so anything here that decides a
 * number — what counts toward a total, which mileage rate applies, how miles
 * round — has to give the same answer Reimburse gives, or a report reads one
 * amount on the phone and another at the desk. The rules as Reimburse has them
 * (public/app.js in that repo):
 *
 *   - Totals count top-level items only (no `parent`) with reimbursable !== false.
 *     reimbursable === false is "on hold": listed on its report, not billed
 *     yet — out of the total, the summary, the package and the reimbursement CSV.
 *   - A receipt is missing when the item is not mileage and receipt_captured is off.
 *   - Mileage uses the newest rate effective on or before the trip's date, and
 *     falls back to the earliest known rate for a trip older than all of them.
 *   - Distance is entered one way and doubled for a round trip; miles round to
 *     0.1 and the amount to the cent.
 *   - A round trip that comes back on a later day saves as two one-way legs,
 *     each on its own date and at that date's rate; the return runs reversed.
 *
 * Pure: no React, no PocketBase, so tests/expenses.test.mjs can load it as is.
 */

import type { Expense, ExpenseCategory, MileageRate } from "./types";

// ---- Dates -----------------------------------------------------------------
// PocketBase returns date fields as "2026-09-15 00:00:00.000Z". Only the day
// matters, and `new Date()` on that string lands on the evening before anywhere
// west of UTC — so days are handled as text and built as local dates.

export const dayOf = (s?: string | null): string => (s ?? "").slice(0, 10);

/** Today in the local calendar, not UTC's (which is tomorrow after 5pm Pacific). */
export function todayIso(now = new Date()): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

function localDate(s?: string | null): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s ?? "");
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}

/** "Sep 15, 2026" */
export function fmtDay(s?: string | null): string {
  const d = localDate(s);
  return d ? d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";
}

/**
 * A moment, not a day — `invoiced_at` is stamped with the full time, as the
 * phone app stamps it — shown as the local calendar day it happened on.
 */
export function fmtInstant(s?: string | null): string {
  const d = s ? new Date(s.replace(" ", "T")) : null;
  return d && !isNaN(d.getTime()) ? d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";
}

/** "Sep 15, 2026 – Sep 19, 2026", or just the start. */
export function fmtPeriod(start?: string | null, end?: string | null): string {
  return fmtDay(start) + (dayOf(end) ? " – " + fmtDay(end) : "");
}

// ---- Money -----------------------------------------------------------------

/** Always two decimals — this is reimbursement, not the rounded-dollar time rollup. */
export function money(n: number | undefined | null): string {
  return "$" + (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const round = (n: number, places: number) => {
  const f = 10 ** places;
  return Math.round(n * f) / f;
};

// ---- Totals ----------------------------------------------------------------

export interface Totals {
  /** Top-level items, reimbursable or not — what the report lists. */
  items: Expense[];
  /** The subset that is paid back — what the totals and the package count. */
  reimbursable: Expense[];
  total: number;
  /** Category name → amount, reimbursable items only. */
  byCat: Record<string, number>;
  missing: number;
  /** Items saved without an amount yet — quick entries still to finish. */
  unpriced: number;
  /** On hold: listed here but not billed yet, so outside `total`. */
  held: number;
  heldTotal: number;
  count: number;
}

export type CategoryName = (id?: string) => string;

export function categoryNamer(cats: ExpenseCategory[]): CategoryName {
  const byId = new Map(cats.map((c) => [c.id, c.name]));
  return (id) => (id && byId.get(id)) || "Other";
}

/** On hold: kept on its report but not billed yet (`reimbursable` is off). */
export function isOnHold(e: Expense): boolean {
  return e.reimbursable === false;
}

/** Saved with no amount: a quick entry to come back to. */
export function needsAmount(e: Expense): boolean {
  return !(Number(e.amount) > 0);
}

export function isMissingReceipt(e: Expense): boolean {
  return !e.is_mileage && !e.receipt_captured;
}

export function totalsFrom(exps: Expense[], catName: CategoryName): Totals {
  const items = exps.filter((e) => !e.parent);
  const reimbursable = items.filter((e) => e.reimbursable !== false);
  const total = round(reimbursable.reduce((s, e) => s + (Number(e.amount) || 0), 0), 2);
  const byCat: Record<string, number> = {};
  for (const e of reimbursable) {
    const c = catName(e.category);
    byCat[c] = round((byCat[c] || 0) + (Number(e.amount) || 0), 2);
  }
  return {
    items, reimbursable, total, byCat,
    missing: items.filter(isMissingReceipt).length,
    unpriced: items.filter(needsAmount).length,
    held: items.length - reimbursable.length,
    heldTotal: round(items.filter(isOnHold).reduce((s, e) => s + (Number(e.amount) || 0), 0), 2),
    count: items.length,
  };
}

/** Expenses grouped by report id, so a list of reports is one pass, not N queries. */
export function byReport(exps: Expense[]): Map<string, Expense[]> {
  const out = new Map<string, Expense[]>();
  for (const e of exps) {
    const list = out.get(e.report);
    if (list) list.push(e); else out.set(e.report, [e]);
  }
  return out;
}

// ---- Mileage ---------------------------------------------------------------

export function rateFor(rates: MileageRate[], date?: string | null): number {
  if (!rates.length) return 0;
  const sorted = [...rates].sort((a, b) => dayOf(a.effective_date).localeCompare(dayOf(b.effective_date)));
  const day = dayOf(date) || todayIso();
  const hit = sorted.filter((r) => dayOf(r.effective_date) <= day).pop();
  return Number((hit ?? sorted[0]).rate) || 0;
}

export function mileageFor(oneWay: number, roundTrip: boolean, rate: number): { miles: number; amount: number } {
  const miles = round((Number(oneWay) || 0) * (roundTrip ? 2 : 1), 1);
  return { miles, amount: round(miles * rate, 2) };
}

/** The one-way distance a saved mileage entry was entered as. */
export function oneWayMiles(e: Pick<Expense, "miles" | "round_trip">): number {
  return round((Number(e.miles) || 0) / (e.round_trip ? 2 : 1), 1);
}

/** "Home → Acme HQ (round trip)" — also written to `description`, as Reimburse does. */
export function routeLabel(from: string, to: string, roundTrip: boolean): string {
  const route = from && to ? `${from} → ${to}` : (from || to || "Mileage");
  return route + (roundTrip ? " (round trip)" : "");
}

export interface MileageLeg {
  date: string;
  from_location: string;
  to_location: string;
  round_trip: boolean;
  miles: number;
  mileage_rate: number;
  amount: number;
  description: string;
}

/**
 * What a mileage form saves. Usually one entry; a round trip back on a later
 * day is two one-way entries, the return reversed and priced at its own date's
 * rate — so no extra field is needed in either app. Reimburse splits the same way.
 */
export function mileageLegs(trip: {
  date: string; returnDate?: string; from: string; to: string; oneWay: number; roundTrip: boolean; rates: MileageRate[];
}): MileageLeg[] {
  const { date, from, to, oneWay, rates } = trip;
  const split = trip.roundTrip && !!trip.returnDate && trip.returnDate !== date;
  const roundTrip = trip.roundTrip && !split;
  const rate = rateFor(rates, date);
  const legs: MileageLeg[] = [{
    date, from_location: from, to_location: to, round_trip: roundTrip, ...mileageFor(oneWay, roundTrip, rate),
    mileage_rate: rate, description: routeLabel(from, to, roundTrip),
  }];
  if (split) {
    const back = rateFor(rates, trip.returnDate);
    legs.push({
      date: trip.returnDate!, from_location: to, to_location: from, round_trip: false, ...mileageFor(oneWay, false, back),
      mileage_rate: back, description: routeLabel(to, from, false) + " (return)",
    });
  }
  return legs;
}

/**
 * Duplicate starts a new, unsaved entry from an existing one, on the report
 * you're looking at. Receipts and the receipt/Quicken/invoice flags belong to
 * the original, so they don't come along.
 */
export function copyOfExpense(src: Expense, reportId: string): Expense {
  const { receipts: _r, parent: _p, receipt_captured: _c, added_to_quicken: _q, added_to_invoice: _i, receiptData: _d, ...rest } = src;
  return { ...rest, id: "", report: reportId };
}

/** Places you have driven from or to, newest first, for autocomplete. */
export function pastPlaces(exps: Expense[], limit = 20): string[] {
  const trips = exps.filter((e) => e.is_mileage).sort((a, b) => dayOf(b.date).localeCompare(dayOf(a.date)));
  return [...new Set(trips.flatMap((e) => [e.from_location, e.to_location]).filter(Boolean) as string[])].slice(0, limit);
}

// ---- Reports ---------------------------------------------------------------

/** "Acme Corp — Sep 2026", Reimburse's auto-name for a report left unnamed. */
export function autoReportName(clientName: string, start?: string | null): string {
  const d = localDate(start);
  return `${clientName || "Report"} — ${d ? d.toLocaleDateString("en-US", { month: "short", year: "numeric" }) : "New"}`;
}

export const isInvoiced = (status?: string) => status === "archived" || status === "invoiced";

// ---- Categories ------------------------------------------------------------

export const sortCategories = (cats: ExpenseCategory[]) =>
  [...cats].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0) || a.name.localeCompare(b.name));

/**
 * Reimbursable items grouped by category name, in the order set under Settings
 * → Expense categories. Drives the package's summary table and receipt pages.
 */
export function packageGroups(items: Expense[], cats: ExpenseCategory[]): [string, Expense[]][] {
  const name = categoryNamer(cats);
  const order = sortCategories(cats).map((c) => c.name);
  const groups = new Map<string, Expense[]>();
  for (const e of items.filter((x) => x.reimbursable !== false)) {
    const k = name(e.category);
    const list = groups.get(k);
    if (list) list.push(e); else groups.set(k, [e]);
  }
  const rank = (n: string) => { const i = order.indexOf(n); return i < 0 ? 999 : i; };
  return [...groups.entries()].sort((a, b) => rank(a[0]) - rank(b[0]));
}

export const isPdf = (name?: string) => /\.pdf$/i.test(name ?? "");

// ---- CSV -------------------------------------------------------------------
// Same columns as Reimburse's exports, so a file made on either device imports
// the same way.

export function toCsv(rows: (string | number)[][]): string {
  return rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\r\n");
}

const amt = (e: Expense) => (Number(e.amount) || 0).toFixed(2);

export function reimbursementCsvRows(t: Totals, catName: CategoryName): (string | number)[][] {
  const rows: (string | number)[][] = [["Date", "Category", "Description", "Amount", "Receipt"]];
  // What is being billed: items on hold wait for a later report.
  for (const e of t.reimbursable) {
    rows.push([dayOf(e.date), catName(e.category), e.description ?? "", amt(e), e.receipt_captured || e.is_mileage ? "yes" : "MISSING"]);
  }
  rows.push([]);
  for (const [c, v] of Object.entries(t.byCat)) rows.push(["", c, "", v.toFixed(2), ""]);
  rows.push(["", "", "Total reimbursable", t.total.toFixed(2), ""]);
  return rows;
}

export function quickenCsvRows(t: Totals, catName: CategoryName): (string | number)[][] {
  const rows: (string | number)[][] = [["Date", "Payee", "Category", "Amount"]];
  for (const e of t.items) rows.push([dayOf(e.date), e.description ?? "", catName(e.category), amt(e)]);
  return rows;
}

export function allExpensesCsvRows(
  reports: { id: string; name: string; clientName: string }[],
  exps: Expense[],
  catName: CategoryName,
): (string | number)[][] {
  const rows: (string | number)[][] = [["Report", "Client", "Date", "Category", "Description", "Amount"]];
  const grouped = byReport(exps);
  for (const r of reports) {
    for (const e of grouped.get(r.id) ?? []) {
      rows.push([r.name, r.clientName, dayOf(e.date), catName(e.category), e.description ?? "", amt(e)]);
    }
  }
  return rows;
}

/** A filesystem-safe stem, as Reimburse names its downloads. */
export const fileStem = (name: string) => (name || "report").replace(/[^\w]+/g, "_");
